import { ActionContext, ActionTree } from 'vuex'
import { LessonStateInterface } from 'src/store/lesson/state'
import { StoreInterface } from 'src/store'
import {
  QUERY_GET_LESSON,
  QUERY_DELETE_LESSON,
  QUERY_GET_MATERIALS, MUTATION_INSERT_ITEM,
  MUTATION_UPSERT_ITEM,
  QUERY_GET_UPCOMING_LESSON, QUERY_GET_LESSON_PLANS, QUERY_ARCHIVE_ITEM, insertLessonPlans
} from 'src/lib/queries/lesson'
import { MutationOptions, QueryOptions } from 'apollo-client'
import { apolloProvider } from 'boot/apollo'
import { DocumentNode } from 'apollo-link'
import buildLessonDataObject from 'src/lib/queries/builder'
import {
  Asset,
  ContentAsset,
  Item,
  ItemId,
  LessonPlan, LevelId, SubjectId,
  User,
  UserId
} from 'able-lib'
import moment from 'moment'
import { getAssetUrlsOnItems, extractObjectIds } from 'src/lib/helper'
import { ROLES } from 'src/lib/constants'
import _ from 'lodash'

const actions: ActionTree<LessonStateInterface, StoreInterface> = {
  async getLesson ({ commit, dispatch }: ActionContext<LessonStateInterface, StoreInterface>, lessonId: ItemId): Promise<any> {
    const options: QueryOptions = {
      query: QUERY_GET_LESSON,
      variables: {
        lessonId
      },
      fetchPolicy: 'network-only'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    console.log('getLesson', data)

    const lesson: Item = data.lesson
    const lessonPlan: LessonPlan = data.lesson.lessonPlan || data.lesson.originalItem.lessonPlan
    // commit('setLesson', lesson)
    commit('SET_ITEM_LESSON_DRAFT', lesson)
    // commit('setItemUser', lesson.itemUsers)

    await dispatch('schedule/populateDataBaseOnLevel', lessonPlan.level, { root: true })
  },

  async getLessonPlans ({ commit }: ActionContext<LessonStateInterface, StoreInterface>, { levelId, subjectId }: { levelId: LevelId; subjectId: SubjectId}): Promise<void> {
    const options: QueryOptions = {
      query: QUERY_GET_LESSON_PLANS,
      variables: {
        lessonPlanLevelId: levelId,
        lessonPlanSubjectId: subjectId
      },
      fetchPolicy: 'network-only'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    console.log('getLessonPlans', data)
    commit('SET_ITEM_LESSON_PLAN_LIST', data.lessonPlans)
  },

  async getLessonPlan ({ commit }: ActionContext<LessonStateInterface, StoreInterface>, lessonId: ItemId): Promise<void> {
    const options: QueryOptions = {
      query: QUERY_GET_LESSON,
      variables: {
        lessonId
      },
      fetchPolicy: 'network-only'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    console.log('getLessonPlan', data)

    const lessonPlan: Item = data.lesson
    commit('SET_ITEM_LESSON_PLAN_DRAFT', lessonPlan)
  },

  async deleteLesson ({ commit, getters }: ActionContext<LessonStateInterface, StoreInterface>) {
    const materialIds = []
    const contentIds = []
    const assetIds = []
    getters.getLesson.items.map(item => {
      materialIds.push(item.id)
      contentIds.push(item.content.id)
      item.content.contentAssets.forEach((contentAsset: ContentAsset) => {
        if (!contentAsset.asset.isShared) assetIds.push(contentAsset.asset.id)
      })
    })

    const options: MutationOptions = {
      mutation: QUERY_DELETE_LESSON,
      variables: {
        itemId: getters.getLesson.id,
        contentId: getters.getLesson.content.id,
        itemMaterials: materialIds,
        contentMaterialIds: contentIds,
        assetMaterialIds: assetIds
      }
    }

    commit('lesson/resetLesson', null, { root: true })
    await apolloProvider.defaultClient.mutate(options)
  },

  async upsertItem (context: ActionContext<LessonStateInterface, StoreInterface>, item: Item): Promise<any> {
    const lessonObjects = buildLessonDataObject(item)

    let mutation: DocumentNode = null
    let variables: { [key: string]: any } = {}

    if (item.id) {
      const extractedIds = extractObjectIds(item)
      mutation = MUTATION_UPSERT_ITEM
      variables = {
        itemId: item.id,
        remainingItems: extractedIds.itemIds,
        remainingContent: extractedIds.contentIds,
        remainingAssets: extractedIds.assetIds,
        remainingParticipatingUsers: extractedIds.remainingParticipatingUserIds,
        remainingMaterialUsers: extractedIds.remainingMaterialUserIds,
        itemDataObject: lessonObjects.data,
        itemConflictClause: lessonObjects.on_conflict
      }
    } else {
      mutation = MUTATION_INSERT_ITEM
      variables = {
        lessonDataObjects: lessonObjects.data,
        lessonConflictClause: lessonObjects.on_conflict
      }
    }

    const options: MutationOptions = {
      mutation,
      variables,
      fetchPolicy: 'no-cache'
    }

    return apolloProvider.defaultClient.mutate(options)
  },

  async upsertLesson ({ commit, dispatch, rootState }: ActionContext<LessonStateInterface, StoreInterface>, lessonDraft: Item) {
    const user: User = rootState.user.user
    commit('addItemUser', { user, role: ROLES.HOST })
    const result = await dispatch('upsertItem', lessonDraft)

    if (lessonDraft.id) {
      commit('SET_ITEM_LESSON_DRAFT', result.data.updatedItem)
      // @ts-ignore
      commit('schedule/UPDATE_SCHEDULE_ON_LIST', _.assign({}, result.data.updatedItem), { root: true })
      console.log(result)
      return result.data.updatedItem
    } else {
      commit('schedule/ADD_SCHEDULE_ON_LIST', result.data.newItem, { root: true })
      return result.data.newItem
    }
  },

  async upsertLessonPlan ({ commit, dispatch }: ActionContext<LessonStateInterface, StoreInterface>, lessonPlanDraft: Item): Promise<Item> {
    const result = await dispatch('upsertItem', lessonPlanDraft)

    if (lessonPlanDraft.id) {
      commit('SET_ITEM_LESSON_PLAN_DRAFT', result.data.updatedItem)
      commit('UPDATE_LESSON_PLAN_ON_LIST', result.data.updatedItem)
      return result.data.updatedItem
    } else {
      commit('ADD_LESSON_PLAN_ON_LIST', result.data.newItem)
      return result.data.newItem
    }
  },

  async insertLessonPlans (context: ActionContext<LessonStateInterface, StoreInterface>, { lessonPlans, levelId, subjectId, year }) {
    const { data } = await insertLessonPlans(lessonPlans, levelId, subjectId, year)
    console.log('insertLessonPlans', data)
  },

  async fetchMaterials ({ commit }: ActionContext<LessonStateInterface, StoreInterface>): Promise<void> {
    const options: QueryOptions = {
      query: QUERY_GET_MATERIALS
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    const materials = data.materials.map((material: Asset) => {
      return {
        asset: material
      }
    })

    console.log(materials)

    commit('setMaterials', materials)
  },

  async upcomingLesson ({ commit, rootState, dispatch }: ActionContext<LessonStateInterface, StoreInterface>): Promise<void> {
    const userId: UserId = rootState.user.user.id
    const startDateTime = moment(new Date()).format('YYYY-MM-DD').toString()
    const endDateTime = moment(new Date()).add(7, 'days').format('YYYY-MM-DD').toString()
    console.debug('Fetching upcoming Lesson ... from ' + startDateTime + ' to ' + endDateTime)

    const options: QueryOptions = {
      query: QUERY_GET_UPCOMING_LESSON,
      variables: {
        startDateTime,
        endDateTime,
        userId
      },
      fetchPolicy: 'no-cache'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    console.debug('local/actions.upcomingLesson', data.lessons)
    const assetUrls = getAssetUrlsOnItems(data.lessons)
    console.debug('local/actions.upcomingLesson', assetUrls)
    commit('local/updateDownloadAsset', assetUrls, { root: true })
    dispatch('local/downloadAssets', null, { root: true })
  },

  async archiveLesson ({ commit }: ActionContext<LessonStateInterface, StoreInterface>, itemId: ItemId): Promise<void> {
    const options: MutationOptions = {
      mutation: QUERY_ARCHIVE_ITEM,
      variables: {
        itemId
      },
      fetchPolicy: 'no-cache'
    }

    await apolloProvider.defaultClient.mutate(options)

    commit('schedule/REMOVE_SCHEDULE_ON_LIST_BY_ID', itemId, { root: true })
  },

  async archiveLessonPlan ({ commit }: ActionContext<LessonStateInterface, StoreInterface>, itemId: ItemId): Promise<void> {
    const options: MutationOptions = {
      mutation: QUERY_ARCHIVE_ITEM,
      variables: {
        itemId
      },
      fetchPolicy: 'no-cache'
    }

    await apolloProvider.defaultClient.mutate(options)

    commit('REMOVE_LESSON_PLAN_ON_LIST_BY_ID', itemId)
  }
}

export default actions
